import { useAuthStore, useConnectionStore } from '@sentinel/web-core';
import { useQueryClient } from '@tanstack/react-query';
import type { MapPositions, MissionDashboard, RealtimeEvent } from '@sentinel/shared';
import { useEffect } from 'react';
import { keys } from '@/core/query/query-keys';
import { moveMarker, movePosition } from './move-marker';

const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

function socketUrl(token: string): string {
  const base = import.meta.env.VITE_WS_URL;
  const absolute = base.startsWith('ws')
    ? base
    : `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${base}`;
  return `${absolute}?access_token=${encodeURIComponent(token)}`;
}

/** Keeps one WebSocket per session with exponential backoff and, on every envelope,
 *  invalidates the queries that show the affected data: the interface updates only what changed. */
export function useRealtime(): void {
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.accessToken);
  const status = useAuthStore((s) => s.status);
  const setRealtime = useConnectionStore((s) => s.setRealtime);
  const markSync = useConnectionStore((s) => s.markSync);

  useEffect(() => {
    if (status !== 'authenticated' || token === null) return;
    let socket: WebSocket | null = null;
    let closed = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      const current = useAuthStore.getState().accessToken;
      if (closed || current === null) return;
      setRealtime('connecting');
      socket = new WebSocket(socketUrl(current));
      socket.onopen = () => {
        attempt = 0;
        setRealtime('connected');
        markSync();
      };
      socket.onmessage = (message) => {
        markSync();
        let event: RealtimeEvent | { type: 'hello' };
        try {
          event = JSON.parse(String(message.data)) as RealtimeEvent | { type: 'hello' };
        } catch {
          return;
        }
        if (event.type === 'hello') return;
        void invalidateFor(queryClient, event);
      };
      socket.onclose = () => {
        setRealtime('disconnected');
        if (closed) return;
        attempt += 1;
        const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** attempt);
        timer = setTimeout(connect, delay);
      };
      socket.onerror = () => socket?.close();
    };
    connect();

    return () => {
      closed = true;
      if (timer !== null) clearTimeout(timer);
      socket?.close();
      setRealtime('disconnected');
    };
  }, [status, token, queryClient, setRealtime, markSync]);
}

async function invalidateFor(
  queryClient: ReturnType<typeof useQueryClient>,
  event: RealtimeEvent,
): Promise<void> {
  const missionId = event.missionId;
  if (event.type === 'drone.telemetry') return onDroneTelemetry(queryClient, event);
  // A tracker moved a team, a resource or itself: its marker moves, nothing else reloads.
  if (event.type === 'position.updated') return movePlaced(queryClient, event);
  // A station called, changed link or went silent: its row and the header reload (ADR 0026).
  if (event.type === 'station.updated')
    return queryClient.invalidateQueries({ queryKey: keys.stations });
  // Only the users it is addressed to receive it: their bell and list reload.
  if (event.type === 'notification.created')
    return queryClient.invalidateQueries({ queryKey: keys.notifications });
  // A message or its receipts: only the channels and threads reload, not the dashboard (§22).
  if (event.type === 'message.created' || event.type === 'message.updated')
    return missionId === null
      ? undefined
      : queryClient.invalidateQueries({ queryKey: keys.comms(missionId) });
  // A sensor of the organization (ADR 0030) belongs to no mission: its list reloads.
  if (event.type === 'device.updated' && missionId === null)
    await queryClient.invalidateQueries({ queryKey: keys.sensors });
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['missions'] }),
    missionId === null
      ? Promise.resolve()
      : queryClient.invalidateQueries({ queryKey: ['mission', missionId] }),
  ]);
}

/** A drone moved or turned its camera: its marker and its view cone change on the mission map
 *  (only them, rule of §22), and the drones screen reloads its list. Without a position (GPS fix
 *  lost) the marker stays at the last one known while the cone keeps following the camera. A
 *  drone the map did not show yet (its first fix) reloads that dashboard once, so it appears. */
async function onDroneTelemetry(
  queryClient: ReturnType<typeof useQueryClient>,
  event: RealtimeEvent,
): Promise<void> {
  await movePlaced(queryClient, event);
  await queryClient.invalidateQueries({ queryKey: ['drones'] });
}

/** Something on the map moved: the marker of the dashboard and of the map page follows it. */
async function movePlaced(
  queryClient: ReturnType<typeof useQueryClient>,
  event: RealtimeEvent,
): Promise<void> {
  const { missionId, entityId, position, view } = event;
  if (missionId !== null && entityId !== null) {
    const key = keys.dashboard(missionId);
    const current = queryClient.getQueryData<MissionDashboard>(key);
    const moved = moveMarker(current, entityId, { position, view: view ?? null });
    const hasPosition = position !== null && position !== undefined;
    const unseen = current !== undefined && moved === current && hasPosition;
    if (unseen) await queryClient.invalidateQueries({ queryKey: key });
    else if (moved !== current) queryClient.setQueryData<MissionDashboard>(key, moved);
    // The map page holds the positions of its visible area: the marker moves there too.
    queryClient.setQueriesData<MapPositions>(
      { queryKey: [...keys.missionMap(missionId), 'positions'] },
      (positions) => movePosition(positions, entityId, { position, view: view ?? null }, event.at),
    );
  }
}
